import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, AuctionLot } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import { calculateNextMinBid } from '../services/bidding.service';
import { submitBid, describeOutcome } from '../services/bidOutcome';
import { useBidOutcomeNotice } from '../components/bidding/useBidOutcomeNotice';
import { BidOutcomeNotice } from '../components/bidding/BidOutcomeNotice';
// Card-auth registration via the gated Cloud Function callable. ALL registration
// now goes through submitSaleRegistration →
// registerForSale ($0 card auth); the direct client saleRegistrations write is
// gone (and denied by rules).
import {
  isRegisteredForSale,
  submitSaleRegistration,
  fetchAcceptJsConfig,
} from '../services/registration.service';
import { getPaymentNonce } from '../services/acceptjs';
import { firstContentImage } from '../utils/lotImages';
import { computeLotDisplay, lotCategoryLabel, lotNumberLabel } from '../utils/lotDisplay';
import { useWatchlist } from '../hooks/useWatchlist';
import WatchButton from '../components/bidding/WatchButton';
import { lotState, isLotBiddableState, soonestLotEndMs, LotState, isUnderReview, DISPUTE_WORDS, closedLotStatus, isDisputeStatus, closedPriceSuffix, relistCount, passesLotFilters, RELIST_VIEW, registrationClosedText } from '../utils/saleState';
import { useAuctionClock } from '../hooks/useAuctionClock';
import { serverNow } from '../utils/serverClock';
import { LotClock, LotStateBadge } from '../components/auction/LotClock';
import SaleDisputeNotice from '../components/SaleDisputeNotice';
import toast from 'react-hot-toast';

/**
 * Public sale page for the Shop Cleanout 050326 parallel test against HiBid.
 * Buyers reach this URL from the email blast — no login required.
 * On first bid we provision a Firebase Auth user from email + name only.
 */

const formatDollars = (cents?: number) => {
  if (!cents && cents !== 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

/** First non-barcode image for the tile; returns '' (-> placeholder) if none.
 *  Detection lives in utils/lotImages, shared with every other tile/modal surface. */
const pickTileImage = (images?: string[]): string => firstContentImage(images);

const SalePage: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();

  // Shared watchlist for the whole grid — one read, passed down to each lot card.
  const { isWatched, toggle: toggleWatch } = useWatchlist();

  // After a successful registration, return the user to where they came from (e.g. the lot
  // they clicked Bid on). Same-origin internal paths only — never an absolute URL.
  const goReturnTo = () => {
    const raw = searchParams.get('returnTo') || '';
    if (raw.startsWith('/') && !raw.startsWith('//')) navigate(raw);
  };

  const [auction, setAuction] = useState<Auction | null>(null);
  const [loading, setLoading] = useState(true);

  // Local-only view controls for the lot grid (no URL params / persistence).
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [noBidsOnly, setNoBidsOnly] = useState(false);
  // null = the visitor hasn't touched it: ON for an ordinary sale, OFF for a disputed one (every
  // lot of a disputed sale is closed, so ON would greet a visitor with an empty grid).
  const [hideClosedChoice, setHideClosedChoice] = useState<boolean | null>(null);
  // The relist view lives in the URL (?show=relist) so it can be linked to from an email.
  const relistOnly = searchParams.get(RELIST_VIEW.param) === RELIST_VIEW.value;
  const setRelistOnly = (on: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set(RELIST_VIEW.param, RELIST_VIEW.value);
    else next.delete(RELIST_VIEW.param);
    setSearchParams(next, { replace: true });
  };

  // Per-sale registration (Phase 1a). A logged-in user must opt in to THIS
  // auction before bidding; nobody is auto-registered.
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (currentUser && auctionId) {
        try {
          const r = await isRegisteredForSale(auctionId, currentUser.uid);
          if (active) setRegistered(r);
        } catch (e) {
          console.error('Registration check failed:', e);
          if (active) setRegistered(false);
        }
      } else if (active) {
        setRegistered(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [currentUser, auctionId]);

  // Card-auth modal state (Phase 1b — SANDBOX).
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cardCode, setCardCode] = useState('');
  const [cardSubmitting, setCardSubmitting] = useState(false);

  const handleRegister = async () => {
    if (!currentUser || !auctionId) return;
    if (auction && registrationClosedText(auction)) return; // no registration path (button is not rendered)
    setRegistering(true);
    try {
      // Try without a card first: the function skips card-auth when the user
      // already has a verified card within the last 6 months.
      const outcome = await submitSaleRegistration(auctionId);
      if (outcome === 'card_required') {
        setShowCardForm(true);
      } else {
        setRegistered(true);
        toast.success('Registered for this sale — you can bid now.');
        goReturnTo();
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleCardSubmit = async () => {
    if (!auctionId) return;
    if (!cardNumber.trim() || !expMonth.trim() || !expYear.trim() || !cardCode.trim()) {
      toast.error('Enter card number, expiry month/year, and CVV.');
      return;
    }
    setCardSubmitting(true);
    try {
      // Card fields go browser -> Authorize.net (Accept.js). We only ever hold
      // the returned one-time nonce, never the PAN.
      const cfg = await fetchAcceptJsConfig();
      const nonce = await getPaymentNonce(cfg, {
        cardNumber: cardNumber.trim(),
        month: expMonth.trim().padStart(2, '0'),
        year: expYear.trim().length === 2 ? `20${expYear.trim()}` : expYear.trim(),
        cardCode: cardCode.trim(),
      });
      const outcome = await submitSaleRegistration(auctionId, nonce);
      if (outcome === 'registered') {
        setRegistered(true);
        setShowCardForm(false);
        setCardNumber('');
        setExpMonth('');
        setExpYear('');
        setCardCode('');
        toast.success('Card verified — you are registered to bid.');
        goReturnTo();
      } else {
        toast.error('Card was not accepted. Please try another card.');
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Card verification failed');
    } finally {
      setCardSubmitting(false);
    }
  };

  // BACKSTOP: some networks (office/school proxies) block Firestore. The SDK does not error
  // in that case, it waits forever, so after 10s of loading we say so and offer the static
  // no-JS catalogue (/catalog/<sale>/, generated by build-catalogue.mjs). A plain <a>, not a
  // router <Link>: /catalog is a real file on the host, not an app route.
  const [slowLoad, setSlowLoad] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setSlowLoad(true), 10000);
    return () => clearTimeout(t);
  }, [loading]);

  // Live Firestore listener for the auction doc so all bids/extensions stream in.
  useEffect(() => {
    if (!auctionId) return;
    const unsub = onSnapshot(
      doc(db, 'auctions', auctionId),
      (snap) => {
        if (!snap.exists()) {
          setAuction(null);
          setLoading(false);
          return;
        }
        const data = snap.data() as any;
        const a: Auction = {
          id: snap.id,
          ...data,
          startDate: data.startDate?.toDate?.() ?? data.startDate,
          endDate: data.endDate?.toDate?.() ?? data.endDate,
          createdAt: data.createdAt?.toDate?.() ?? data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt,
          lots: (data.lots || []).map((l: any) => ({
            ...l,
            scheduledEndTime: l.scheduledEndTime?.toDate?.() ?? l.scheduledEndTime,
            actualEndTime: l.actualEndTime?.toDate?.() ?? l.actualEndTime,
            lastBidAt: l.lastBidAt?.toDate?.() ?? l.lastBidAt,
            lastExtensionAt: l.lastExtensionAt?.toDate?.() ?? l.lastExtensionAt,
            createdAt: l.createdAt?.toDate?.() ?? l.createdAt,
          })),
        } as Auction;
        setAuction(a);
        setLoading(false);
      },
      (err) => {
        console.error('Sale page snapshot error:', err);
        setLoadFailed(true);
        setLoading(false);
      }
    );
    return unsub;
  }, [auctionId]);

  // ONE clock for the whole grid (adaptive: 1s when a lot is near, 15s otherwise).
  // Replaces the old per-page 1s tick; every tile derives its state from this single
  // number, so no two tiles can disagree about "now".
  const soonestEnd = useMemo(
    () => soonestLotEndMs(auction?.lots || [], serverNow()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auction],
  );
  const nowMs = useAuctionClock(soonestEnd);

  // Derived grid: shared with the homepage via computeLotDisplay (no drift).
  // Client-side only — never mutates the auction doc.
  const { lots: baseLots, totalCount } = useMemo(
    () => computeLotDisplay(auction?.lots || [], { groupByCategory, noBidsOnly }),
    [auction, groupByCategory, noBidsOnly],
  );

  // Hide closed lots so the action stays in the first viewport as the sale runs down.
  // Applied AFTER computeLotDisplay so the shared helper — and the homepage, which also
  // calls it — are untouched.
  //
  // 'settling' is deliberately NOT treated as closed. isLotClosedState() covers only
  // closed_sold and closed_unsold, so a lot inside its 5-second settling grace stays on
  // screen and can extend back into play when a last-second bid's snapshot arrives. Do
  // not replace this with a `now > endTime` comparison — that would hide lots that are
  // about to extend.
  //
  // The three closed-lot states (saleState.ts) are filtered separately by passesLotFilters:
  // hiding closed lots never hides one on the relist list, and the relist view shows only those.
  const hideClosed = hideClosedChoice ?? !isUnderReview(auction);
  const displayLots = useMemo(() => {
    if (!auction) return baseLots;
    return baseLots.filter((l) => passesLotFilters(auction, l, lotState(l, auction, nowMs), { hideClosed, relistOnly }));
  }, [baseLots, hideClosed, relistOnly, auction, nowMs]);

  const shownCount = displayLots.length;

  const catalogueFallback = (
    <div className="mt-4 max-w-xl mx-auto p-4 border border-yellow-300 bg-yellow-50 rounded text-left text-sm">
      <p className="font-semibold">We can't reach the live sale from your connection.</p>
      <p className="mt-1">
        Some office and school networks block the service that carries live bidding. You can still see
        every lot in the plain catalogue. To bid, try another network — your phone on mobile data usually works.
      </p>
      <p className="mt-2">
        <a href={`/catalog/${auctionId}/`} className="text-primary-600 underline font-semibold">
          Open the plain catalogue for this sale →
        </a>
      </p>
    </div>
  );

  if (loading)
    return (
      <div className="p-8 text-center">
        Loading sale…
        {slowLoad && catalogueFallback}
      </div>
    );
  if (loadFailed && !auction)
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold">This sale didn't load</h1>
        {catalogueFallback}
      </div>
    );
  if (!auction)
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold">Sale not found</h1>
        <p className="text-gray-600 mt-2">Check the link or contact the auction house.</p>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4">
      {isUnderReview(auction) && auction.disputeNotice && (
        <SaleDisputeNotice text={auction.disputeNotice} relistCount={relistCount(auction)} relistUpdatedAt={auction.disputedLotsUpdatedAt} relistUpdateNote={auction.disputedLotsUpdateNote} />
      )}
      <header className="mb-6">
        <h1 className="text-3xl font-bold">{auction.title}</h1>
        {auction.description && <p className="text-gray-600 mt-1">{auction.description}</p>}
        <p className="text-sm text-gray-500 mt-1">
          Closes {auction.endDate ? new Date(auction.endDate).toLocaleString() : 'TBD'} ·{' '}
          {auction.lots?.length || 0} lots · 3-minute soft close
        </p>
        <Link to={`/sale/${auction.id}/info`} className="inline-block text-sm text-primary-600 hover:underline mt-1">
          Sale Info &amp; Terms →
        </Link>
        {/* On a sale with registrationClosed or bidOffFor the closed line above/below wins and this
            goes ENTIRELY: sending someone to create an account for a sale they cannot join leads nowhere. */}
        {!currentUser && !registrationClosedText(auction) && (
          // No guest bidding: a signed-out visitor is sent to sign in or register (KYC + card on file).
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3">
            <p>
              To bid, sign in or create a bidder account. Every bidder registers and puts a card on
              file before bidding — nothing is charged unless you win.
            </p>
            <p className="mt-1">
              <Link to={`/login?next=${encodeURIComponent(`/sale/${auction.id}`)}`} className="font-semibold underline">
                Sign in
              </Link>
              {' · '}
              <Link to="/signup" className="font-semibold underline">
                Create a bidder account
              </Link>
            </p>
          </div>
        )}
        {userProfile && (
          <p className="text-xs text-gray-500 mt-2">
            Bidding as {userProfile.displayName || userProfile.email}
            {userProfile.bidderNumber ? ` (#${userProfile.bidderNumber})` : ''}
          </p>
        )}
        {/* Registration closed / Bid Off: a plain line in place of the Register button, shown to
            everyone not already registered (signed in or not). See registrationGate. */}
        {!registered && registrationClosedText(auction) && (
          <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 mt-3">
            {registrationClosedText(auction)}
          </p>
        )}
        {currentUser && !registered && !registrationClosedText(auction) && (
          <div className="mt-3 flex items-center gap-3 bg-blue-50 border border-blue-200 rounded px-3 py-2">
            <p className="text-sm text-blue-800 flex-1">
              You must register for this sale before you can bid.
            </p>
            <button
              type="button"
              onClick={handleRegister}
              disabled={registering}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            >
              {registering ? 'Registering…' : 'Register for this sale'}
            </button>
          </div>
        )}
        {currentUser && registered && (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 mt-3">
            ✓ You're registered for this sale — bidding is open to you.
          </p>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => setGroupByCategory((v) => !v)}
          className={`px-3 py-2 text-sm rounded border ${
            groupByCategory
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Sort by Category
        </button>
        <button
          type="button"
          onClick={() => setNoBidsOnly((v) => !v)}
          className={`px-3 py-2 text-sm rounded border ${
            noBidsOnly
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Show Lots w No Bids
        </button>
        <button
          type="button"
          onClick={() => setHideClosedChoice(!hideClosed)}
          disabled={relistOnly}
          className={`px-3 py-2 text-sm rounded border disabled:opacity-50 ${
            hideClosed && !relistOnly
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {isUnderReview(auction) ? DISPUTE_WORDS.hideLots : 'Hide Closed Lots'}
        </button>
        {(isUnderReview(auction) || relistCount(auction) > 0 || relistOnly) && (
          <button
            type="button"
            onClick={() => setRelistOnly(!relistOnly)}
            aria-pressed={relistOnly}
            className={`px-3 py-2 text-sm rounded border ${
              relistOnly
                ? 'bg-orange-600 text-white border-orange-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {DISPUTE_WORDS.showRelistOnly}{relistCount(auction) > 0 ? ` (${relistCount(auction)})` : ''}
          </button>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          Showing {shownCount} of {totalCount} lots
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {displayLots.length === 0 ? (
          <p className="col-span-full text-center text-gray-500 py-10">
            {relistOnly && relistCount(auction) === 0 ? DISPUTE_WORDS.noRelistLots : 'No lots match the current filter.'}
          </p>
        ) : (
          displayLots.map((lot, i) => {
            const showHeader =
              groupByCategory && (i === 0 || lotCategoryLabel(displayLots[i - 1]) !== lotCategoryLabel(lot));
            return (
              <React.Fragment key={lot.id}>
                {showHeader && (
                  <h2 className="col-span-full text-lg font-bold text-gray-700 border-b border-gray-200 pb-1 mt-2">
                    {lotCategoryLabel(lot)}
                  </h2>
                )}
                <LotCard
                  auction={auction}
                  lot={lot}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  registered={registered}
                  nowMs={nowMs}
                  watched={isWatched(auction.id, lot.id)}
                  onToggleWatch={() =>
                    toggleWatch({
                      auctionId: auction.id,
                      lotId: lot.id,
                      lotNumber: lot.lotNumber,
                      lotTitle: lot.title,
                      addedAt: Date.now(),
                    })
                  }
                />
              </React.Fragment>
            );
          })
        )}
      </div>

      {showCardForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-1">Verify a card to register</h2>
            <p className="text-xs text-gray-500 mb-4">
              A $0 authorization confirms your card — you are not charged. Your card
              details go directly to our processor and never touch our servers.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card number</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              placeholder="4111 1111 1111 1111"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className="w-full mb-3 px-3 py-2 border rounded text-sm"
            />
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Exp. month</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM"
                  value={expMonth}
                  onChange={(e) => setExpMonth(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Exp. year</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY"
                  value={expYear}
                  onChange={(e) => setExpYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div className="w-24">
                <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder="123"
                  value={cardCode}
                  onChange={(e) => setCardCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={handleCardSubmit}
                disabled={cardSubmitting}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {cardSubmitting ? 'Verifying…' : 'Verify & register'}
              </button>
              <button
                type="button"
                onClick={() => setShowCardForm(false)}
                disabled={cardSubmitting}
                className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface LotCardProps {
  auction: Auction;
  lot: AuctionLot;
  currentUser: any;
  userProfile: any;
  registered: boolean;
  nowMs: number;
  watched: boolean;
  onToggleWatch: () => void;
}

const LotCard: React.FC<LotCardProps> = ({
  auction,
  lot,
  currentUser,
  userProfile,
  registered,
  nowMs,
  watched,
  onToggleWatch,
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { notice: outcome, show: showOutcome, clear: clearOutcome } = useBidOutcomeNotice(auction.id, lot.id);

  // Shared vocabulary — the SAME function the homepage tile uses, so the two
  // surfaces can no longer disagree about whether this lot is closing.
  const state: LotState = lotState(lot, auction, nowMs);
  const closed = !isLotBiddableState(state);
  // Dispute display (saleState.ts): 'closed' | 'under-review' | 'to-be-relisted'.
  const closedStatus = closedLotStatus(auction, lot);

  const currentBidCents = lot.currentBid ?? lot.startingBid ?? 0;
  const nextMinCents = lot.bidCount && lot.bidCount > 0
    ? calculateNextMinBid(currentBidCents, auction.bidIncrements)
    : (lot.startingBid ?? 0);

  const isMyBid = !!(currentUser && lot.currentBidder === currentUser.uid);

  const cover = pickTileImage(lot.images);

  const onBidClick = () => {
    // Registration closed / Bid Off: nobody new can register, so no guest form and no register
    // prompt — say so instead. A registered bidder bids as normal.
    const closedText = registrationClosedText(auction);
    if (closedText && (!currentUser || !registered)) {
      toast.error(closedText);
      return;
    }
    if (!currentUser) {
      // No guest bidding: sign in (or create an account), then come back to this sale.
      navigate(`/login?next=${encodeURIComponent(`/sale/${auction.id}`)}`);
      return;
    }
    if (!registered) {
      toast.error('Register for this sale first — use the "Register for this sale" button at the top.');
      return;
    }
    void doBid();
  };

  const doBid = async () => {
    setSubmitting(true);
    clearOutcome();
    try {
      const user = currentUser;
      if (!user) return; // unreachable: onBidClick sends signed-out visitors to sign in
      const displayName = userProfile?.displayName || userProfile?.email || 'Bidder';
      // ATTEMPT — the server decides. Nothing here refuses on a local clock or minimum.
      const o = await submitBid({ auctionId: auction.id, lotId: lot.id, amountCents: nextMinCents, bidderId: user.uid, bidderName: displayName });
      if (o.kind === 'accepted') toast.success(describeOutcome(o, nextMinCents, lot.lotNumber).text);
      else showOutcome(o, nextMinCents, lot.lotNumber);
    } catch (e: any) {
      console.error(e);
      showOutcome({ kind: 'error', detail: e?.message }, nextMinCents, lot.lotNumber);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded shadow-sm border overflow-hidden flex flex-col ${
      state.kind === 'closing_now' || state.kind === 'settling'
        ? 'border-red-500 ring-1 ring-red-300'
        : isMyBid ? 'border-green-500' : 'border-gray-200'
    }`}>
      <div className="aspect-square bg-gray-100 relative">
        <Link to={`/auctions/${auction.id}/lots/${lot.id}`} className="block w-full h-full">
          {cover ? (
            <img src={cover} alt={lot.title} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
              No image
            </div>
          )}
        </Link>
        {/* One badge, one vocabulary — EXTENDED ×N / CLOSING / CLOSED all come from
            the same LotState the clock below renders, so they cannot contradict it. */}
        <LotStateBadge state={state} closedStatus={closedStatus} className="absolute top-2 right-2" />
        {/* Watch toggle — build a shortlist of lots to bid on. Clear of the corner badges. */}
        <WatchButton variant="icon" watched={watched} onToggle={onToggleWatch} className="absolute bottom-2 left-2 z-10" />
      </div>

      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          {/* Was "Lot 7" in muted text-xs — the only place in the app using that casing.
              Now the shared lotNumberLabel(), sized to be scannable down a long grid. */}
          <span className="text-sm font-bold text-gray-900 font-mono">{lotNumberLabel(lot)}</span>
          <LotClock state={state} closedStatus={closedStatus} />
        </div>
        <h3 className="text-sm font-semibold mt-1 line-clamp-2">
          <Link to={`/auctions/${auction.id}/lots/${lot.id}`} className="hover:text-blue-700 hover:underline">
            {lot.title}
          </Link>
        </h3>
        <div className="mt-auto pt-2 flex items-end justify-between">
          <div>
            {state.kind === 'closed_sold' && isDisputeStatus(closedStatus) ? (
              <>
                {/* Disputed sale: the amount stays public; only "Sold" / "You won" are wrong. */}
                <div className="text-xs text-gray-500">{DISPUTE_WORDS.highBid}</div>
                <div className="text-lg font-bold">
                  {formatDollars(state.hammerCents ?? 0)}
                  <span className="ml-1 text-xs font-semibold text-amber-800">{closedPriceSuffix(closedStatus)}</span>
                </div>
                {isMyBid ? (
                  <div className="text-xs font-semibold text-gray-700">{DISPUTE_WORDS.youAreHighBidder}</div>
                ) : state.winnerBidderNumber ? (
                  <div className="text-xs text-gray-500">Bidder #{state.winnerBidderNumber}</div>
                ) : null}
              </>
            ) : state.kind === 'closed_sold' ? (
              <>
                <div className="text-xs text-gray-500">Sold</div>
                <div className="text-lg font-bold">{formatDollars(state.hammerCents ?? 0)}</div>
                {/* PUBLIC bidder number only — never a name (see saleState privacy note). */}
                {isMyBid ? (
                  <div className="text-xs font-semibold text-green-700">You won</div>
                ) : state.winnerBidderNumber ? (
                  <div className="text-xs text-gray-500">Bidder #{state.winnerBidderNumber}</div>
                ) : null}
              </>
            ) : state.kind === 'closed_unsold' ? (
              <>
                <div className="text-xs text-gray-500">Unsold</div>
                <div className="text-lg font-bold text-gray-400">No bids</div>
              </>
            ) : (
              <>
                <div className="text-xs text-gray-500">Current bid</div>
                <div className="text-lg font-bold">{formatDollars(currentBidCents)}</div>
                {isMyBid && <div className="text-xs text-green-600">You're winning</div>}
              </>
            )}
          </div>
          {!closed && (
            <button
              type="button"
              onClick={onBidClick}
              disabled={submitting}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Placing…' : `Bid ${formatDollars(nextMinCents)}`}
            </button>
          )}
        </div>

        <BidOutcomeNotice outcome={outcome} onDismiss={clearOutcome} className="mt-2" />
      </div>
    </div>
  );
};

export default SalePage;
